import AdvancedSearch from '@app/components/AdvancedSearch'
import BidirectionalSort from '@app/components/BidirectionalSort'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import ListingTypeBox from '@app/components/ListingTypeBox'
import ListingTypeSelector from '@app/components/ListingTypeSelector'
import PipelineStatusSelector from '@app/components/PipelineStatusSelector'
import SearchBar from '@app/components/SearchBar'
import { IBrokeragePipeline } from '@app/models/BrokeragePipeline'
import { IGlobalConfigurationState, IGlobalHbUserState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatAddressSingleLine, formatCurrency, formatDate, humanize } from '@app/utils/formatting'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, RouteComponentProps } from 'react-router-dom'
import { Column } from 'react-virtualized'
import { Button, Col, FormGroup, Input, Label, Nav, NavItem, NavLink } from 'reactstrap'
import { actionCreators, IPipelineListState, PipelineScope } from '../modules/pipelineList'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

type ComponentProps = IPipelineListState & IGlobalConfigurationState & IGlobalHbUserState & typeof actionCreators & RouteComponentProps<{}>

export default class PipelineListPage extends React.PureComponent<ComponentProps> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public componentDidMount() {
    if (this.props.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public componentDidUpdate(prevProps: ComponentProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public render() {
    return (
      <DocumentTitle title="Brokerage Pipeline">
        <div className="container d-flex flex-column h-100 pt-3">
          <div>
            <div className="mb-2 d-flex flex-row justify-content-between">
              <h3>Brokerage Pipeline</h3>
              <Link to="pipeline/quickAdd" className="align-self-center">
                <Button size="sm" color="primary">
                  <FontAwesomeIcon icon={['fal', 'plus']} fixedWidth />
                  <span>Quick Add Pipeline</span>
                </Button>
              </Link>
            </div>
            <SearchBar onChange={this._onChangeFilter} placeholder="Looking for a pipeline?" size="sm" value={this.props.filterText} />
            <AdvancedSearch className="mt-1 justify-content-evenly" expand="md">
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>Status</Label>
                  <PipelineStatusSelector bsSize="sm" includeAll onChange={this._onChangeStatus} value={this.props.status} />
                </FormGroup>
              </Col>
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>Listing Type</Label>
                  <ListingTypeSelector bsSize="sm" includeAll value={this.props.listingType} onChange={this._onChangeListingType} />
                </FormGroup>
              </Col>
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>Selling Agent</Label>
                  <Input type="select" bsSize="sm" value={this.props.sellingAgent} onChange={this._onChangeSellingAgent}>
                    <option key="user1" value="All">
                      All
                    </option>
                    {this.props.hbContacts
                      .map((c: any, i) => (
                        <option key={`user-${i}`} value={c.contactId}>
                          {c.name}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>Sort By</Label>
                  <Input type="select" bsSize="sm" value={this.props.sortOrder} onChange={this._onChangeSortOrder}>
                    {this.props.configuration.brokeragePipelineSortOptions
                      .map((c, i) => (
                        <option key={`sort-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <BidirectionalSort md={4} xl={4} orderBy={this.props.orderBy} changeOrdering={this._onChangeOrderBy} />
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>Closing Year</Label>
                  <Input type="select" bsSize="sm" value={this.props.year} onChange={this._onChangeYear}>
                    <option key="all-years" value="All">
                      All
                    </option>
                    {Array.from({ length: new Date().getFullYear() - 2015 + 1 }, (_, i) => 2015 + i)
                      .reverse()
                      .map((c, i) => (
                        <option key={`year-${i}`} value={c}>
                          {c}
                        </option>
                      ))}
                  </Input>
                </FormGroup>
              </Col>
            </AdvancedSearch>
            <div className="mt-4">
              <strong>My Total Projected Profit: {formatCurrency(this.props.totalPotentialProfit)}</strong>
            </div>
            <div className="mb-3 mt-3">
              <Nav tabs>
                <NavItem>
                  <NavLink active={this.props.scope === PipelineScope.Mine} onClick={() => this._onChangeScope(PipelineScope.Mine)}>
                    {PipelineScope.Mine}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink active={this.props.scope === PipelineScope.All} onClick={() => this._onChangeScope(PipelineScope.All)}>
                    {PipelineScope.All}
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>
          <div>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={50}
              loadItems={this.props.loadPipeline}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellRenderer={({ rowData }) => {
                  const pipeline = rowData as IBrokeragePipeline

                  return <ListingTypeBox status={pipeline.status} type={pipeline.associatedListing.type} />
                }}
                dataKey="type"
                width={50}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const pipeline = rowData as IBrokeragePipeline
                  return (
                    <div>
                      <div className="overflow-text" style={{ fontWeight: 'bold' }}>{`${pipeline.name}, ${pipeline.associatedProperty.name}`}</div>
                      <div className="overflow-text">{formatAddressSingleLine(pipeline.associatedProperty.address)}</div>
                    </div>
                  )
                }}
                dataKey="property"
                flexGrow={1}
                width={100}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const pipeline = rowData as IBrokeragePipeline
                  return formatCurrency(pipeline.salesPrice)
                }}
                dataKey="listingPrice"
                headerClassName="d-none d-lg-block"
                className="d-none d-lg-block"
                label="Sales Price"
                width={120}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const pipeline = rowData as IBrokeragePipeline
                  return formatDate(pipeline.dueDiligenceExpirationDate, 'MM/DD/YYYY')
                }}
                dataKey="expirationDate"
                headerClassName="d-none d-lg-block"
                className="d-none d-lg-block"
                label="DD Exp. Date"
                width={150}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const pipeline = rowData as IBrokeragePipeline
                  return pipeline.currentUserFeeAmount ? formatCurrency(pipeline.currentUserFeeAmount) : '-'
                }}
                dataKey="commission"
                headerClassName="d-none d-lg-block"
                className="d-none d-lg-block"
                label="Commission"
                width={120}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const pipeline = rowData as IBrokeragePipeline
                  return pipeline.closingDate ? formatDate(pipeline.closingDate, 'MM/DD/YYYY') : '-'
                }}
                dataKey="closingDate"
                headerClassName="d-none d-lg-block"
                className="d-none d-lg-block"
                label="Closing Date"
                width={150}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _noRowsRenderer() {
    return <div>No pipelines</div>
  }

  private _onRowClicked(item: IBrokeragePipeline) {
    window.open(`${window.location.pathname}/${item.id}`)
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeListingType(newListingType: string) {
    this.props.setListingType(newListingType)
    this._reloadData()
  }

  private _onChangeSellingAgent(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSellingAgent(event.target.value)
    this._reloadData()
  }

  private _onChangeScope(newScope: PipelineScope) {
    this.props.setScope(newScope)
    this._reloadData()
  }

  private _onChangeStatus(newStatus: string) {
    this.props.setStatus(newStatus)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _onChangeYear(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value === 'All') {
      this.props.setYear()
    } else {
      this.props.setYear(parseInt(event.target.value))
    }
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
